import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { Form, Formik } from 'formik'
import { useMutation } from '@tanstack/react-query'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import {
  Box,
  Typography,
  makeStyles,
  IconButton,
  InputAdornment,
} from '@material-ui/core'

import effects from 'app/effects/effects'
import { useError } from 'commons/useError'
import { SECONDARY } from 'app/constants/button'
import { passwordSchemaValidation } from 'commons/schemaValidation'

import LabelInput from 'app/componentV4/Labels/LabelInput'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import LinkToSignup from 'app/componentV4/Link/LinkToSignup'
import ContainedButton from 'app/componentV4/Button/ContainedButton'

const useStyles = makeStyles({
  button: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
    },
  },
  input: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
    },
  },
})

const ResetPasswordV4 = (props) => {
  const classes = useStyles()
  const { errors } = useError()
  const { formatMessage } = useIntl()

  const resetPassword = useMutation({
    mutationFn: async (value) => {
      const response = await effects.apis.authV2.resetPassword({
        email: props.params.email,
        password: value.password,
        code: props.params.code,
      })
      return response
    },
    onError: (error) => {
      errors(formatMessage({ id: `error.${error.response.data.error.name}` }))
    },
  })

  return (
    <div className="w-full space-y-10">
      {/* Header */}
      <Typography className="text-18 font-500 text-primary text-center">
        {'Create new password'}
      </Typography>

      <Box className="rounded-8 py-5 px-10 bg-primary-200">
        <Typography className="text-16 text-primary text-center">
          {'Enter new password for'}
        </Typography>
        <Typography className="text-16 font-500 text-primary text-center break-all">
          {props.params.email}
        </Typography>
      </Box>

      <Formik
        initialValues={{
          password: '',
          confirmPassword: '',
          isShowPassword: false,
          isShowConfirmPassword: false,
        }}
        validationSchema={passwordSchemaValidation(formatMessage)}
        onSubmit={async (values, { resetForm }) => {
          await resetPassword.mutate(values, {
            onSuccess: () => {
              resetForm()
              props.onSubmit()
            },
          })
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
        }) => (
          <Form className="space-y-10">
            <LabelInput
              label={'Enter Password'}
              required
              className="w-full mb-0"
            >
              <SimpleInput
                className={classes.input}
                type={values.isShowPassword ? 'text' : 'password'}
                id="password-input"
                name="password"
                placeholder={'Enter Password'}
                value={values.password}
                size="medium"
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setFieldValue('isShowPassword', !values.isShowPassword)
                      }
                    >
                      {values.isShowPassword ? (
                        <Visibility
                          fontSize="small"
                          className="text-grey-200"
                        />
                      ) : (
                        <VisibilityOff
                          fontSize="small"
                          className="text-grey-200"
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{ 'aria-label': 'Reset Password' }}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />
            </LabelInput>

            <LabelInput
              label={'Confirm Password'}
              required
              className="w-full mb-0"
            >
              <SimpleInput
                className={classes.input}
                type={values.isShowConfirmPassword ? 'text' : 'password'}
                id="confirm-password-input"
                name="confirmPassword"
                placeholder={'Confirm Password'}
                value={values.confirmPassword}
                size="medium"
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setFieldValue(
                          'isShowConfirmPassword',
                          !values.isShowConfirmPassword,
                        )
                      }
                    >
                      {values.isShowConfirmPassword ? (
                        <Visibility
                          fontSize="small"
                          className="text-grey-200"
                        />
                      ) : (
                        <VisibilityOff
                          fontSize="small"
                          className="text-grey-200"
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{ 'aria-label': 'Confirm Reset Password' }}
                error={
                  touched.confirmPassword && Boolean(errors.confirmPassword)
                }
                helperText={touched.confirmPassword && errors.confirmPassword}
              />
            </LabelInput>

            {/* Continue button */}
            <div className="flex justify-center mt-10">
              <ContainedButton
                type="submit"
                color={SECONDARY}
                className="w-full h-48"
                isLoading={resetPassword.isPending}
                disabled={resetPassword.isPending}
              >
                {'Continue'}
              </ContainedButton>
            </div>
          </Form>
        )}
      </Formik>
      <LinkToSignup />
    </div>
  )
}

ResetPasswordV4.propTypes = {
  onSubmit: PropTypes.func,
  params: PropTypes.shape({
    code: PropTypes.string,
    email: PropTypes.string,
  }),
}

export default ResetPasswordV4
